import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Box,
    Button,
    Grid,
    Paper,
    Typography,
    IconButton,
    MenuItem,
    Select,
    TextField,
    Divider,
    Dialog,
    Switch,
    FormControlLabel,
    DialogTitle,
    DialogContent,
    DialogActions
} from "@mui/material";
import { MdDownload, MdSend, MdPrint } from "react-icons/md";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { post, put } from "../utility/fetch";
import TemplateAlt1 from "./LabReportTemplates/TemplateAlt1";
import TemplateAlt2 from "./LabReportTemplates/TemplateAlt2";
import TemplateAlt3 from "./LabReportTemplates/TemplateAlt3";
import TemplateAlt4 from "./LabReportTemplates/TemplateAlt4";
import notification from "../utility/notification";
import { calculateAge, DisplayContent, formatSavedTable } from "../utility/general";

/**
 * LabReportDesigner
 *
 * Props:
 * - reportData: same structure used in current LabReports (contains patientTestReports)
 * - initialTemplateId?: number 1-5
 * - initialColor?: string (hex)
 * - onSaved?: (res) => void
 *
 * Behavior:
 * - shows selector for 5 designs and color picker
 * - auto-selects passed initialTemplateId/initialColor if provided
 * - submit button persists selection via /labreporttemplate POST (payload { templateId, color })
 * - each patientTestReport shows Print and Send buttons that export/send only that report with letterhead
 *
 * Note: uses sessionStorage keys:
 * - letterhead (or letterHead / logoBase64)
 * - healthCareProviderName
 * - location
 * - phone
 */

const TEMPLATE_OPTIONS = [
    { id: 1, name: "Classic" },
    { id: 2, name: "Modern" },
    { id: 3, name: "Minimal" },
    { id: 4, name: "Professional" },
    { id: 5, name: "Compact" }
];

const getLetterheadDataUrl = () => {
    const keys = ["letterhead", "letterHead", "providerLetterhead", "letterHeadBase64", "logoBase64"];
    let pic = null;
    for (const k of keys) {
        const v = sessionStorage.getItem(k);
        if (v) {
            pic = v;
            break;
        }
    }
    if (!pic) return null;
    if (pic.startsWith("data:")) return pic;
    if (/^[A-Za-z0-9+/=\s]+$/.test(pic) && pic.length > 100) {
        return `data:image/png;base64,${pic.replace(/\s+/g, "")}`;
    }
    return pic;
};

const providerNameFromSession = () => sessionStorage.getItem("healthCareProviderName") || "Healthcare Provider";
const providerLocationFromSession = () => sessionStorage.getItem("location") || "";
const providerPhoneFromSession = () => sessionStorage.getItem("phone") || "";

const buildReportWrapper = ({ titleNodeHtml, contentNodeHtml, letterhead }) => {
    const wrapper = document.createElement("div");
    wrapper.style.background = "#fff";
    wrapper.style.padding = "25px";
    wrapper.style.boxSizing = "border-box";
    wrapper.style.width = "800px";
    wrapper.style.fontFamily = "Arial, Helvetica, sans-serif";

    if (letterhead) {
        const img = document.createElement("img");
        img.src = letterhead;
        img.style.width = "50px";
        img.style.objectFit = "cover";
        img.style.marginBottom = "10px";
        img.setAttribute("data-letterhead", "true");
        wrapper.appendChild(img);
    }

    // inject supplied html strings
    const titleContainer = document.createElement("div");
    titleContainer.innerHTML = titleNodeHtml || "";
    wrapper.appendChild(titleContainer);

    const contentContainer = document.createElement("div");
    contentContainer.innerHTML = contentNodeHtml || "";
    wrapper.appendChild(contentContainer);

    return wrapper;
};

const captureNodeToPdf = async (node, a4 = true) => {
    // node should be appended to DOM (offscreen) before calling
    const canvas = await html2canvas(node, {
        scale: 2, // Use a scale of 2 for a good balance of quality and performance.
        useCORS: true,
        backgroundColor: window.getComputedStyle(node).backgroundColor || "#ffffff",
        logging: false,
        imageTimeout: 15000,
        allowTaint: false
    });

    if (!a4) {
        const pdf = new jsPDF("p", "pt", [canvas.width, canvas.height]);
        const imgData = canvas.toDataURL("image/png", 1.0);
        pdf.addImage(imgData, "PNG", 0, 0, canvas.width, canvas.height);
        return pdf;
    }

    // paginate into a4
    const pdf = new jsPDF("p", "pt", "a4");
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();

    const canvasWidth = canvas.width;
    const canvasHeight = canvas.height;

    const ratio = canvasWidth / pdfWidth;
    const pageHeightPx = Math.floor(pdfHeight * ratio);

    let remaining = canvasHeight;
    let position = 0;
    let pageIndex = 0;

    while (remaining > 0) {
        const chunk = Math.min(pageHeightPx, remaining);
        const pageCanvas = document.createElement("canvas");
        pageCanvas.width = canvasWidth;
        pageCanvas.height = chunk;
        const ctx = pageCanvas.getContext("2d");
        ctx.drawImage(canvas, 0, position, canvasWidth, chunk, 0, 0, canvasWidth, chunk);
        const img = pageCanvas.toDataURL("image/png", 1.0);
        const pagePdfHeight = chunk / ratio;
        if (pageIndex > 0) pdf.addPage();
        pdf.addImage(img, "PNG", 0, 0, pdfWidth, pagePdfHeight);
        remaining -= chunk;
        position += chunk;
        pageIndex++;
    }

    return pdf;
};

// Updated to accept Blob directly and clean token
const sendPdf = async ({ pdfBlob, recipientEmail, recipientName }) => {
    const formData = new FormData();
    formData.append("PatientName", recipientName || "");
    formData.append("PatientEmail", recipientEmail || "");

    // Append the Blob directly. 
    // Important: The 3rd argument is the filename, which is required for the backend to treat it as a file.
    formData.append("PdfReport", pdfBlob, `Lab_Report_${new Date().toISOString().split('T')[0]}.pdf`);

    // Get token and strip potential double quotes if it was stored via JSON.stringify
    const token = sessionStorage.getItem('token');
    const cleanToken = token ? token.replace(/^"|"$/g, '') : '';

    console.log('Sending report with formData:', formData);

    const res = await fetch("https://api.greenzonetechnologies.com.ng/connectedhealthlabapi/api/patientlabreport/send-labreport", {
        method: "POST",
        body: formData,
        headers: {
            "Authorization": `Bearer ${cleanToken}`
        }
    });

    if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`Send failed: ${res.status} ${errorText}`);
    }
    return res.json();
};

const TemplateLayout = ({ children, title, color, letterheadUrl, labName, labLocation }) => (
    <Paper sx={{ p: 2, mb: 2 }}>
        <Box display={"flex"} sx={{ mb: "10px" }}>
            {letterheadUrl && (
                <Box component="img" src={letterheadUrl} alt="letterhead" sx={{ width: "70px", mb: 1, objectFit: "cover" }} data-letterhead="true" />
            )}
            <Box sx={{ ml: "12px" }}>
                <Typography variant="h6" sx={{ fontWeight: 800, color }}>{labName}</Typography>
                {labLocation && <Typography variant="body2" sx={{ color: "#666" }}>{labLocation}</Typography>}
            </Box>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
            <Box>
                <Typography variant="h6" sx={{ fontWeight: 800, color }}>{labName}</Typography>
                {labLocation && <Typography variant="body2" sx={{ color: "#666" }}>{labLocation}</Typography>}
            </Box>
            <Box sx={{ textAlign: "right", color: "#777" }}>
                <Typography variant="caption">Generated</Typography>
                <Typography variant="caption" sx={{ display: "block", fontWeight: 700 }}>{new Date().toLocaleDateString()}</Typography>
            </Box>
        </Box>

        <Divider sx={{ mb: 1 }} />

        <Box>
            <Typography variant="subtitle1" sx={{ color }}>{title}</Typography>
            <Box sx={{ mt: 1 }}>{children}</Box>
        </Box>
    </Paper>
);

const PrintStyles = () => {
    React.useEffect(() => {
        const styleId = 'lab-report-print-styles';
        if (!document.getElementById(styleId)) {
            const style = document.createElement('style');
            style.id = styleId;
            style.innerHTML = `
        @media print {
          body, html {
            width: 100%;
            height: auto;
            background: #fff;
          }
          .no-print { display: none !important; }
          .printable-content {
            width: 100% !important;
            max-width: 100% !important;
            box-shadow: none !important;
          }
        }
      `;
            document.head.appendChild(style);
        }
    }, []);
    return null;
};

// const PrintStyles = () => {
//     React.useEffect(() => {
//         const styleId = 'lab-report-print-styles';
//         if (!document.getElementById(styleId)) {
//             const style = document.createElement('style');
//             style.id = styleId;
//             style.innerHTML = `
//         @media print {
//           .no-print { display: none !important; }
//         }
//       `;
//             document.head.appendChild(style);
//         }
//     }, []);
//     return null;
// };

// hide elements marked .no-print inside a node (used before html2canvas capture)
const hideNoPrintElements = (node) => {
    if (!node) return;
    node.querySelectorAll('.no-print').forEach(el => {
        // keep original inline display in case you want to restore later
        el.dataset._origDisplay = el.style.display || '';
        el.style.display = 'none';
    });
};

export default function LabReportDesigner({
    reportData = {},
    initialTemplateId = null,
    initialColor = "#2e7d32",
    review = false,
    onSaved = () => { },
    template
}) {
    const [templateId, setTemplateId] = useState(initialTemplateId || 1);
    const [color, setColor] = useState(initialColor || "#2e7d32");
    const [saving, setSaving] = useState(false);
    const [sendDialogOpen, setSendDialogOpen] = useState(false);
    const [sendTarget, setSendTarget] = useState({ id: null, name: "", email: "" });
    // track compact mode per report id so toggling affects only the clicked report
    const [compactModes, setCompactModes] = useState({});
    const wrapperRef = useRef();

    useEffect(() => {
        if (initialTemplateId) setTemplateId(initialTemplateId);
        if (initialColor) setColor(initialColor);
        // eslint-disable-next-line
    }, [initialTemplateId, initialColor]);

    const letterheadUrl = useMemo(() => getLetterheadDataUrl(), []);
    const labName = useMemo(() => providerNameFromSession(), []);
    const labLocation = useMemo(() => providerLocationFromSession(), []);

    console.log(templateId)

    const persistSelection = async () => {
        setSaving(true);
        try {
            const payload = { formId: templateId, colorId: color };
            let res;
            if (initialTemplateId) {
                // update existing template
                const response = await put(`/labreporttemplate/update/${template?.id}`,
                    payload
                );

                console.log("Update response:", response);
                if (response.statusCode === 200) {
                    notification({
                        type: 'success',
                        message: response.data.message
                    })
                }
                // if (!response.ok) throw new Error(`Update failed: ${response.status}`);
                // res = await response.json();
                // alert("Template updated");
            } else {
                // create new template
                res = await post("/labreporttemplate", payload);
                notification({
                    type: 'success',
                    message: "Template saved successfully"
                });
                // alert("Template saved");
            }
            onSaved(res);
        } catch (e) {
            console.error(e);
            notification({
                type: 'error',
                message: "Failed to save template"
            })
            // alert("Failed to save template");
        } finally {
            setSaving(false);
        }
    };

    // print/download whole rendered designer area
    const handleDownloadAll = async () => {
        if (!wrapperRef.current) return;
        // clone
        const cloned = wrapperRef.current.cloneNode(true);

        // hide controls/buttons before capture so html2canvas/pdf won't include them
        hideNoPrintElements(cloned);

        cloned.style.position = "absolute";
        cloned.style.left = "-9999px";
        document.body.appendChild(cloned);
        try {
            const pdf = await captureNodeToPdf(cloned, true);
            pdf.save(`LabReports_${new Date().toISOString().split("T")[0]}.pdf`);
        } catch (e) {
            console.error(e);
            alert("Failed to create PDF");
        } finally {
            document.body.removeChild(cloned);
        }
    };

    // print/send single patientTestReport by id
    const handleDownloadSingle = async (reportId) => {
        if (!wrapperRef.current) return;
        const source = wrapperRef.current.querySelector(`[data-report-id="${reportId}"]`);
        if (!source) {
            alert("Report not found in DOM");
            return;
        }
        const cloned = source.cloneNode(true);
        // wrap with letterhead/header
        const titleHtml = `<div style="margin-bottom:8px;"><strong>${labName}</strong><div style="color:#666">${labLocation}</div></div>`;
        const contentHtml = cloned.innerHTML;
        const wrapper = buildReportWrapper({ titleNodeHtml: titleHtml, contentNodeHtml: contentHtml, letterhead: letterheadUrl });

        // hide no-print items (download/send buttons) inside the wrapper prior to capture
        hideNoPrintElements(wrapper);

        wrapper.style.position = "absolute";
        wrapper.style.left = "-9999px";
        document.body.appendChild(wrapper);
        try {
            const pdf = await captureNodeToPdf(wrapper, true);
            pdf.save(`Report_${reportId}_${new Date().toISOString().split("T")[0]}.pdf`);
        } catch (e) {
            console.error(e);
            alert("Failed to create single report PDF");
        } finally {
            document.body.removeChild(wrapper);
        }
    };

    const openSendDialogFor = (reportId) => {
        const rpt = (reportData?.patientTestReports || []).find(r => String(r.id) === String(reportId));
        setSendTarget({ id: reportId, name: rpt?.patientFullName || "", email: "" });
        setSendDialogOpen(true);
    };

    const handleSendSingle = async () => {
        const { id, name, email } = sendTarget;
        if (!email) {
            alert("Enter recipient email");
            return;
        }
        // find source DOM
        const source = wrapperRef.current.querySelector(`[data-report-id="${id}"]`);
        if (!source) {
            alert("Report not found");
            return;
        }

        const cloned = source.cloneNode(true);
        const titleHtml = `<div style="margin-bottom:8px;"><strong>${labName}</strong><div style="color:#666">${labLocation}</div></div>`;
        const contentHtml = cloned.innerHTML;
        const wrapper = buildReportWrapper({ titleNodeHtml: titleHtml, contentNodeHtml: contentHtml, letterhead: letterheadUrl });

        // hide no-print items (buttons) before capturing PDF for sending
        hideNoPrintElements(wrapper);

        wrapper.style.position = "absolute";
        wrapper.style.left = "-9999px";
        document.body.appendChild(wrapper);

        try {
            const pdf = await captureNodeToPdf(wrapper, true);

            // Use native output('blob') which is much more reliable than manual base64 conversion
            const pdfArrayBuffer = pdf.output("arraybuffer");
            const pdfBlob = new Blob([pdfArrayBuffer], { type: "application/pdf" });

            console.log("PDF size:", pdfBlob.size);
            console.log("PDF type:", pdfBlob.type);

            await sendPdf({ pdfBlob, recipientEmail: email, recipientName: name });
            notification({
                type: 'success',
                message: "Report sent successfully"
            });
            setSendDialogOpen(false);
        } catch (e) {
            console.error(e);
            notification({
                type: 'error',
                message: "Failed to send report. Please try again."
            });
        } finally {
            if (wrapper.parentNode) document.body.removeChild(wrapper);
        }
    };

    // -- Simple template renderers --
    const renderReportRows = (report) => {
        console.log(report)
        const getAllRefRanges = (testDetails) => {
            // Return all available reference ranges with their categories
            const ranges = {};
            if (testDetails?.refRange_Adult) ranges.Adult = testDetails.refRange_Adult;
            if (testDetails?.refRange_Children) ranges.Children = testDetails.refRange_Children;
            if (testDetails?.refRange_FeMale) ranges.Female = testDetails.refRange_FeMale;
            if (testDetails?.refRange_Male) ranges.Male = testDetails.refRange_Male;
            if (testDetails?.refRange_Elderly) ranges.Elderly = testDetails.refRange_Elderly;
            return ranges;
        };

        const getPrimaryRefRange = (testDetails) => {
            // Priority: Adult > Children > Female > Male > Elderly > default
            if (testDetails?.refRange_Adult) return testDetails.refRange_Adult;
            if (testDetails?.refRange_Children) return testDetails.refRange_Children;
            if (testDetails?.refRange_FeMale) return testDetails.refRange_FeMale;
            if (testDetails?.refRange_Male) return testDetails.refRange_Male;
            if (testDetails?.refRange_Elderly) return testDetails.refRange_Elderly;
            return testDetails?.normalRefRange || "-";
        };

        // per-report compact flag (default false)
        const isCompact = Boolean(compactModes[report.id]);
        const toggleCompactFor = (id) => {
            setCompactModes(prev => ({ ...prev, [id]: !prev[id] }));
        };

        return (
            <Box data-report-id={report.id} sx={{ p: 1 }}>
                {
                    !review && 
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{report?.internalLabService?.name || "Test Panel"}</Typography>
                        <Box className="no-print" sx={{ display: "flex", gap: 1 }}>
                            <Button
                                size="small"
                                onClick={() => handleDownloadSingle(report.id)}
                                startIcon={<MdDownload />}
                                variant="outlined"
                                sx={{ borderColor: color, color: color }}
                            >
                                Download
                            </Button>

                            <Grid item>
                                <FormControlLabel
                                    control={<Switch sx={{ color: "green" }} checked={isCompact} onChange={() => toggleCompactFor(report.id)} />}
                                    label="Compact Mode"
                                    sx={{ ml: 2, color: 'green' }}
                                />
                            </Grid>

                            <Button
                                size="small"
                                onClick={() => openSendDialogFor(report.id)}
                                variant="contained"
                                startIcon={<MdSend />}
                                sx={{ backgroundColor: color, "&:hover": { backgroundColor: color } }}
                            >
                                Send
                            </Button>
                        </Box>
                    </Box>
                }
                
                {/* <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{report?.internalLabService?.name || "Test Panel"}</Typography> */}

                <Box sx={{ mt: 1 }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                            <tr style={{ background: color, color: "#fff" }}>
                                <th style={{ textAlign: "left", padding: 8, width: isCompact ? '60%' : '40%' }}>Parameter</th>
                                <th style={{ textAlign: "left", padding: 8, width: isCompact ? '40%' : '20%' }}>Result</th>
                                {!isCompact && <th style={{ textAlign: "left", padding: 8, width: '15%' }}>Unit</th>}
                                {!isCompact && <th style={{ textAlign: "left", padding: 8, width: '25%' }}>Reference Range</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {report.labTestServiceResults?.map((r) => {
                                const primaryRange = getPrimaryRefRange(r.testDetails);
                                const allRanges = getAllRefRanges(r.testDetails);
                                const hasMultipleRanges = Object.keys(allRanges).length > 1;

                                return (
                                    <tr key={r.id}>
                                        <td style={{ padding: 8, borderTop: "1px solid #eee" }}>{r.testDetails?.parameter}</td>
                                        <td style={{ padding: 8, borderTop: "1px solid #eee", fontWeight: 700, color: color }}><DisplayContent htmlContent={formatSavedTable(r.result)} /></td>
                                        {!isCompact && <td style={{ padding: 8, borderTop: "1px solid #eee" }}>{r.testDetails?.unitOfMeasurement}</td>}
                                        {!isCompact && <td style={{ padding: 8, borderTop: "1px solid #eee" }}>
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 600, mb: hasMultipleRanges ? 1 : 0 }}>{primaryRange}</Typography>
                                                {hasMultipleRanges && (
                                                    <Box sx={{ mt: 1, pt: 1, borderTop: `1px solid ${color}` }}>
                                                        <Typography variant="caption" sx={{ fontWeight: 700, color, display: "block", mb: 0.5 }}>Other Categories:</Typography>
                                                        {Object.entries(allRanges).map(([category, range]) => (
                                                            <Box key={category} sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                                                                <Typography variant="caption" sx={{ color: "#666" }}>{category}:</Typography>
                                                                <Typography variant="caption" sx={{ fontWeight: 600 }}>{range}</Typography>
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                )}
                                            </Box>
                                        </td>}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </Box>

                {report.comment && <Typography variant="body2" sx={{ mt: 1, color: "#555" }} className="no-print"><strong>Note:</strong> {report.comment}</Typography>}
            </Box>
        );
    };


    const TemplateRenderer = ({ id }) => {
        const title = `Laboratory Report`;
        const commonProps = { reportData, color, letterheadUrl, labName, labLocation, renderRow: renderReportRows };

        switch (Number(id)) {
            case 1:
                return (
                    <TemplateLayout title={title} color={color} letterheadUrl={letterheadUrl} labName={labName} labLocation={labLocation}>
                        <Typography variant="body2" sx={{ mb: 1 }}>Patient: {reportData?.walkInPatient?.firstName + " " + reportData?.walkInPatient?.lastName || reportData?.patientFullName || reportData?.fullName || "N/A"}</Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>Gender: {reportData?.walkInPatient?.gender || "N/A"}</Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>Age: {calculateAge(reportData?.walkInPatient?.dateOfBirth) || "N/A"}</Typography>
                        {(reportData?.patientTestReports || []).map((r) => <Box key={r.id}>{renderReportRows(r)}</Box>)}
                    </TemplateLayout>
                );
            case 2:
                return <TemplateAlt1 {...commonProps} />;
            case 3:
                return <TemplateAlt2 {...commonProps} />;
            case 4:
                return <TemplateAlt3 {...commonProps} />;
            case 5:
                return <TemplateAlt4 {...commonProps} />;
            default:
                return null;
        }
    };

    return (
        <Box>
            <PrintStyles />

            {!review && <Paper sx={{ p: 2, mb: 2 }} className="no-print">
                <Grid container spacing={2} alignItems="center">
                    <Grid item>
                        <Typography variant="subtitle1">Design</Typography>
                        <Select size="small" value={templateId} onChange={(e) => setTemplateId(Number(e.target.value))}>
                            {TEMPLATE_OPTIONS.map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
                        </Select>
                    </Grid>

                    <Grid item>
                        <Typography variant="subtitle1">Theme color</Typography>
                        <TextField type="color" size="small" value={color} onChange={(e) => setColor(e.target.value)} sx={{ width: 72 }} />
                    </Grid>

                    <Grid item>
                        <Button variant="contained" onClick={persistSelection} disabled={saving}>
                            {initialTemplateId ? "Update" : "Save"}
                        </Button>
                    </Grid>

                    <Grid item>
                        <Button startIcon={<MdDownload />} onClick={handleDownloadAll}>Download All</Button>
                    </Grid>
                </Grid>
            </Paper>}

            {/* Render selected template */}
            <Box ref={wrapperRef}>
                <TemplateRenderer id={templateId} />
            </Box>

            <Dialog open={sendDialogOpen} onClose={() => setSendDialogOpen(false)}>
                <DialogTitle>Send Report</DialogTitle>
                <DialogContent>
                    <TextField fullWidth label="Recipient name" value={sendTarget.name} onChange={(e) => setSendTarget(s => ({ ...s, name: e.target.value }))} sx={{ mb: 2, mt: 1 }} />
                    <TextField fullWidth label="Recipient email" value={sendTarget.email} onChange={(e) => setSendTarget(s => ({ ...s, email: e.target.value }))} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSendDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSendSingle}>Send</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}